import { callFirebaseFunction } from 'boot/firebase';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { chain, find, get, map, omit, shuffle } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import { TARGET_DATA_TYPES, TARGET_STATUSES } from 'src/constants/targets';
import DataDB from 'src/fb/data-db';
import GenericDb from 'src/fb/generic-db';
import ProgramsDB from 'src/fb/programs-db';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import AppointmentsMixin from 'src/mixins/AppointmentsMixin';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';
import { workflows } from 'src/store/workflows';
import AppUtils from 'src/utils/AppUtils';
import { BACKUP_DATA } from 'src/utils/backupUtils';
import { logEvent } from 'src/utils/LogEvent';

const DISABLED_STATUSES = new Set([
  TARGET_STATUSES.ABORTED,
  TARGET_STATUSES.CLOSED,
  TARGET_STATUSES.HOLD,
  TARGET_STATUSES.WAITING,
]);

const RATE_AND_DURATION_TYPES = [TARGET_DATA_TYPES.RATE, TARGET_DATA_TYPES.DURATION];

export default {

  mixins: [AppointmentsMixin, Auth, Interactions, Targets],

  computed: {
    ...mapState(auth, ['currentClient', 'hasOfflineModeClients']),
    ...mapState(network, ['offlineMode']),
    ...mapState(programs, ['programs', 'cachedGroupPrograms']),
    ...mapState(workflows, ['workflows']),
  },

  methods: {
    ...mapActions(workflows, ['reloadWorkflows']),

    /**
   * Update session to reflect that it is started
   * @param {object} session
   * @param {string} userId
   * @param {string} userName
   * @param {string|null} initialProgramId
   * @param {string|null} [appointmentId]
   * @param {string|null} [sessionGroupId]
   * @param {TransactionMethods|null} transactionMethods supports transaction, but use only write operations on this
   * area because number of read operations reach firestore rules limitations for transactions
   * https://firebase.google.com/docs/firestore/manage-data/transactions#security_rules_limits
   */
    async startRunningSession(
      session,
      userId,
      userName,
      initialProgramId,
      appointmentId = null,
      sessionGroupId = null,
      transactionMethods = null,
    ) {
      const sessionsDb = new SessionsDB();
      const sessionChanges = {
        id: session.id,
        currentProgram: initialProgramId,
        runBy: userId,
        runByName: userName,
        runTimestamp: serverTimestamp(),
        durationOffsetTimestamp: serverTimestamp(),
        durationIsPaused: false,
        status: 'running',
      };

      // We shouldn't link the appointment if it's already linked to any session activity
      if (
        appointmentId
        && !(await this.isAppointmentLinkedToActivities(appointmentId, session.clinic))
      ) {
        sessionChanges.appointment = appointmentId;
      }

      if (sessionGroupId) {
        sessionChanges.sessionGroup = sessionGroupId;
      }

      const updatedSession = { ...session, ...sessionChanges };

      try {
        const sessionProgramsDb = new SessionProgramsDB(session.id);
        const sessionPrograms = await sessionProgramsDb.readAll();

        await Promise.all(
          map(
            sessionPrograms,
            (sessionProgram) => this.initializeSessionProgramData(
              updatedSession,
              sessionProgram,
              transactionMethods,
            ),
          ),
        );
        if (transactionMethods) {
          await transactionMethods.update(sessionsDb, { data: sessionChanges });
        } else {
          await sessionsDb.update(sessionChanges);
        }
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err);
        console.error(err);
      }

      if (this.offlineMode) {
        await sessionsDb.addNewSessionActivity(updatedSession, userId, userName);
      }
    }, // startRunningSession()

    async getProgramForSession(session, programId, transactionMethods = null) {
      try {
        let program = null;
        if (session.client === this.currentClient.id) {
          program = find(this.programs, { id: programId });
        } else {
          program = chain(this.cachedGroupPrograms)
            .get(session.client)
            .find({ id: programId })
            .value();
        }

        if (!program) {
          const programsDb = new ProgramsDB();
          program = transactionMethods
            ? await transactionMethods.read(programsDb, { id: programId })
            : await programsDb.read(programId);
        }
        if (!program || program.isDeleted) {
          return null;
        }

        if (!chain(program).get('targets').isArray().value()) {
          const programTargetsDb = new ProgramTargetsDB(program.id);
          const targets = transactionMethods
            ? await transactionMethods.readAll(programTargetsDb)
            : await programTargetsDb.readAll();
          return {
            ...program,
            targets,
          };
        }

        return program;
      } catch (error) {
        return null;
      }
    }, // getProgramForSession()

    /**
     * Creates missing data documents for active session
     */
    async recreateMissingDataDocumentsBySessionPrograms() {
      await this.recreateMissingDataDocuments(this.activeSession, this.activeSessionPrograms, this.data);
    }, // recreateMissingDataDocumentsBySessionPrograms()

    /**
     * Creates missing data documents for staff session
     */
    async recreateMissingDataDocumentsByStaffSessionPrograms(staffSession, staffSessionPrograms, staffData) {
      await this.recreateMissingDataDocuments(staffSession, staffSessionPrograms, staffData);
    }, // recreateMissingDataDocumentsByStaffSessionPrograms()

    /**
     * Creates missing data documents by sessionPrograms
     */
    async recreateMissingDataDocuments(currentSession, currentSessionPrograms, data) {
      if (!currentSession?.id || !currentSessionPrograms?.length) {
        return;
      }
      let sessionProgramsWithMissingDataIds;
      try {
        this.$q.loading.show();
        const activeSessionPrograms = currentSessionPrograms.map(
          (sessionProgram) => ({ ...sessionProgram, program: sessionProgram.program.id }),
        );
        const sessionProgramsWithMissingDataDocuments = activeSessionPrograms.filter(
          (sessionProgram) => !data.some((dataItem) => dataItem.id === sessionProgram.id),
        );
        sessionProgramsWithMissingDataIds = sessionProgramsWithMissingDataDocuments.map((program) => program.id);
        await Promise.all(
          map(
            sessionProgramsWithMissingDataDocuments,
            (sessionProgram) => this.initializeSessionProgramData(currentSession, sessionProgram),
          ),
        );
        logEvent('Data documents reinitialized', {
          severity: 'info',
          context: {
            context_data: { activeSession: currentSession, missingDataDocuments: sessionProgramsWithMissingDataIds },
          },
        });
        this.notifySuccess(this.$t('run.collectData.dataUpdatedSuccessfullyMessage'));
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err, {
          context: {
            context_data: { activeSession: currentSession, missingDataDocuments: sessionProgramsWithMissingDataIds },
          },
        });
        console.error(err);
      } finally {
        this.$q.loading.hide();
      }
    }, // recreateMissingDataDocuments()

    // @todo: move refreshFromOriginalSession method to firebase functions
    /**
     * Aligns active session with the original session by
     * adding or deleting programs
     */
    async refreshFromOriginalSession() {
      const originalSessionId = this.activeSession?.originalSessionId;
      const currentSessionId = this.activeSession?.id;
      if (!originalSessionId || !currentSessionId || !this.activeSessionPrograms?.length) {
        return;
      }

      const existingProgramsIds = this.activeSessionPrograms.map((sessionProgram) => {
        return sessionProgram.program?.id;
      }).filter((value) => !!value);

      const sessionProgramsDb = new SessionProgramsDB(originalSessionId);
      const dataDb = new DataDB();
      const originalSessionPrograms = await sessionProgramsDb.readAll();

      sessionProgramsDb.setSessionId(currentSessionId);
      const currentProgramsIds = (await sessionProgramsDb.readAll()).map((sessionProgram) => sessionProgram.program);
      const sessionProgramsToCreate = originalSessionPrograms.filter((sessionProgram) => {
        return !existingProgramsIds.includes(sessionProgram.program)
          && !currentProgramsIds.includes(sessionProgram.program);
      });
      const sessionProgramsIds = originalSessionPrograms.map((sessionProgram) => sessionProgram.program);
      const sessionProgramsToRemove = currentProgramsIds.filter((currentProgram) => {
        return !sessionProgramsIds.includes(currentProgram);
      }).map((sessionProgram) => {
        return this.activeSessionPrograms.find((session) => session.program?.id === sessionProgram);
      });

      // Synchronize visibility
      const visibilityUpdate = this.activeSessionPrograms.map((activeProgram) => {
        const originalProgram = originalSessionPrograms.find((sessionProgram) => {
          return sessionProgram.program === activeProgram.program?.id;
        });
        if (originalProgram && activeProgram.hidden !== originalProgram.hidden) {
          return sessionProgramsDb.update({ id: activeProgram.id, hidden: originalProgram.hidden });
        }
        return null;
      });

      // Duplicate all active child sessionPrograms
      const createActions = sessionProgramsToCreate.map(async (sessionProgram) => {
        const program = this.programs.find((value) => value?.id === sessionProgram?.program);

        if (!program || !ACTIVE_PROGRAM_STATUSES.includes(program.status)) {
          return null;
        }

        const sessionProgramsObj = {
          ...sessionProgram,
          id: null,
        };

        const createdSessionProgram = await sessionProgramsDb.create(sessionProgramsObj);

        return this.initializeSessionProgramData(this.activeSession, createdSessionProgram);
      });

      // Delete removed programs
      const genericDb = new GenericDb();
      const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);
      const deleteActions = sessionProgramsToRemove.map(async (sessionProgram) => {
        await genericDb.executeTransaction(async (transactionMethods) => {
          await transactionMethods.delete(sessionProgramsDb, sessionProgram.id);
          await transactionMethods.update(dataDb, {
            data: {
              id: sessionProgram.id,
              isDeleted: true,
              deleteTimestamp: serverTimestamp(),
            },
          });
        });
        return sessionBackupDataDb.delete(`backupData_${sessionProgram.id}`, true);
      });

      await Promise.all([...createActions, ...deleteActions, ...visibilityUpdate]);
    }, // refreshFromOriginalSession()

    async getRelevantWorkflows(program) {
      const requiredWorkflowIds = new Set(
        (program.targets || []).reduce((acc, target) => {
          if (target.workflow) {
            acc.push(target.workflow);
          }

          if (target.defaultWorkflow) {
            acc.push(target.defaultWorkflow);
          }

          return acc;
        }, program.workflow ? [program.workflow] : []),
      );

      if (!requiredWorkflowIds.size) {
        return {};
      }

      await this.reloadWorkflows([...requiredWorkflowIds]);

      return this.workflows.reduce((acc, wf) => {
        if (requiredWorkflowIds.has(wf.id)) {
          acc[wf.id] = wf;
        }
        return acc;
      }, {});
    }, // getRelevantWorkflows()

    /**
     * Determines the required 'minTrials' value based on cascading inheritance logic:
     * Target -> Parent -> Program (prioritizing manual adjustments over workflow settings).
     *
     * @param {Object} target - The current target object.
     * @param {Object|null} parent - The parent target object, if any.
     * @param {Object} program - The program object containing the target.
     * @param {Object} relevantWorkflows - A dictionary of workflow definitions.
     * @returns {number|null} The resolved minTrials value, or null if not found.
     */
    extractMinTrials(target, parent, program, relevantWorkflows) {
      const { status, workflow, workflowAdjustments } = target;
      const getWorkflowMinTrials = (workflowId) => relevantWorkflows[workflowId]?.statuses?.[status]?.minTrials ?? null;

      // Priority 1: Target Adjustments
      const targetAdj = workflowAdjustments?.[status]?.minTrials;
      if (targetAdj) {
        return targetAdj;
      }

      // Priority 2: Target Workflow
      if (workflow) {
        return getWorkflowMinTrials(workflow);
      }

      // Priority 3: Parent Adjustments
      if (parent) {
        const parentAdj = parent.defaultWorkflowAdjustments?.[status]?.minTrials;
        if (parentAdj) {
          return parentAdj;
        }

        // Priority 4: Parent Workflow
        if (parent.defaultWorkflow) {
          return getWorkflowMinTrials(parent.defaultWorkflow);
        }
      }

      // Priority 5: Program Adjustments
      const programAdj = program.workflowAdjustments?.[status]?.minTrials;
      if (programAdj) {
        return programAdj;
      }

      // Priority 6: Program Workflow
      if (program.workflow) {
        return getWorkflowMinTrials(program.workflow);
      }

      return null;
    }, // extractMinTrials()

    getTargetsInfo(program, relevantWorkflows) {
      const targets = program.targets || [];
      const targetsMap = new Map(targets.map((t) => [t.id, t]));

      const parentTargetIds = new Set(
        targets.map((t) => t.parentTarget).filter(Boolean),
      );

      return targets.reduce((acc, target) => {
        // Skip if the target status is disabled, type is rate/duration,
        // or if the current target is a parent.
        if (
          DISABLED_STATUSES.has(target.status)
          || RATE_AND_DURATION_TYPES.includes(target.dataType)
          || parentTargetIds.has(target.id)
        ) {
          return acc;
        }

        const parent = target.parentTarget ? targetsMap.get(target.parentTarget) : null;
        const minTrials = this.extractMinTrials(target, parent, program, relevantWorkflows);

        if (minTrials !== null) {
          acc[target.id] = {
            status: target.status,
            minTrials,
          };
        }

        return acc;
      }, {});
    }, // getTargetsInfo()

    /**
     * Creates data documents by provided session and sessionProgram
     * @param {object} session
     * @param {object} sessionProgram
     * @param {TransactionMethods|null} transactionMethods supports transaction, but use only write operations on this
     * area because number of read operations reach firestore rules limitations for transactions
     * https://firebase.google.com/docs/firestore/manage-data/transactions#security_rules_limits
     */
    async initializeSessionProgramData(session, sessionProgram, transactionMethods = null) {
      const program = await this.getProgramForSession(session, sessionProgram.program);
      if (!program) {
        return null;
      }

      const relevantWorkflows = await this.getRelevantWorkflows(program);
      const targetsInfo = this.getTargetsInfo(program, relevantWorkflows);

      const dataDb = new DataDB();
      let data = [];

      let sessionProgramData = {
        client: session.client,
        clinic: session.clinic,
        program: sessionProgram.program,
        session: session.id,
        sessionProgram: sessionProgram.id,
        user: session.runBy || null,
        programStatus: program.status,
        dataType: this.getDataType(program.type),
        notes: '',
        dataIsTimecoded: true,
        targetsInfo,
      };

      sessionProgramData.targetsLabels = this.getTargetsLabelsKeyedById(program.targets);

      if (['frequency'].includes(program.type)
        && (sessionProgram.pinned || (get(program, 'timerAutoStart', false)))) {
        // If the program is frequency and the session program is pinned or
        // the program has timerAutoStart we should initialize a zero-count for the
        // targets
        const sessionProgramSelectedTargets = get(sessionProgram, 'selectedTargets', false);

        data = chain(program)
          .get('targets')
          .reject((target) => (chain(sessionProgram).get('hiddenTargets').includes(target.id)).value())
          // Reject statuses that are not used in session.
          // If no targets were found it indicates that the program has custom status, so we should always include it
          // as custom statuses cannot be hidden
          .filter(
            (target) => {
              return sessionProgramSelectedTargets
                ? get(sessionProgramSelectedTargets, target.status, true)
                : false;
            },
          )
          .map((target) => ({
            target: target.id,
          }))
          .value();
      }

      if (program.promptLevelTemplate) {
        sessionProgramData.promptLevelTemplate = program.promptLevelTemplate;
      }
      if (['frequency', 'interval', 'esdm'].includes(program.type)) {
        sessionProgramData.duration = 0;
        sessionProgramData.isTimerPaused = !get(program, 'timerAutoStart', false);
        sessionProgramData.timerOffsetTimestamp = sessionProgramData.isTimerPaused
          ? null
          : session.durationOffsetTimestamp || Timestamp.now();
      }
      if (['interval', 'esdm'].includes(program.type)) {
        sessionProgramData.currentInterval = 1;
      }
      if (program.type === 'esdm') {
        sessionProgramData = {
          ...sessionProgramData,
          ...chain(program)
            .get('targets')
            .map((target) => ([
              `intervalData_${target.id}`,
              [],
            ]))
            .fromPairs()
            .value(),
        };
      }
      if (program.type === 'asr') {
        sessionProgramData.currentTrial = 1;
        sessionProgramData.currentProgramFormItem = null;
      }

      sessionProgramData.data = data;
      // Save the data
      if (transactionMethods) {
        return transactionMethods.create(dataDb, { data: sessionProgramData, id: sessionProgram.id });
      }

      return dataDb.create(sessionProgramData, sessionProgram.id);
    }, // initializeSessionProgramData()

    /**
     * Used in Offline Mode.
     *
     * @param {string} sessionId
     */
    async prepareSessionOnClientSide(sessionId) {
      const sessionsDb = new SessionsDB();
      const sessionDoc = await sessionsDb.read(sessionId);
      const session = omit(sessionDoc, 'sessionHistory');

      if (['preparing', 'created'].includes(session.status)) {
        const sessionProgramsDb = new SessionProgramsDB(sessionId);
        const sessionPrograms = await sessionProgramsDb.readOrdered();

        const addHiddenTargetsActions = map(sessionPrograms, async (sessionProgram) => {
          const hiddenTarget = await this.getHiddenTargets(sessionProgram.id);
          return sessionProgramsDb.update({ id: sessionProgram.id, hiddenTarget });
        });

        await AppUtils.allSettled(addHiddenTargetsActions);

        return sessionId;
      }

      if ((session.scheduleType !== 'recurring') || (session.status !== 'scheduled')) {
        this.notifyNegative('Invalid session status');
        return;
      }

      const newSession = await sessionsDb.create({
        ...session,
        appointment: null,
        shuffle: false,
        originalSessionId: sessionId,
        duration: 0,
        status: 'preparing',
        supervisedBy: null,
        supervisedByName: null,
        createTimestamp: Timestamp.now(),
        updateTimestamp: Timestamp.now(),
      });

      const sessionProgramsDb = new SessionProgramsDB(sessionId);
      let sessionPrograms = await sessionProgramsDb.readOrdered();

      if (session.shuffle) {
        sessionPrograms = shuffle(sessionPrograms);
      }

      const addHiddenTargetsActions = map(sessionPrograms, async (sessionProgram, idx) => {
        const hiddenTargets = await this.getHiddenTargets(sessionProgram.program);

        const newSessionProgramsDb = new SessionProgramsDB(newSession.id);
        return newSessionProgramsDb.create({
          ...sessionProgram,
          hiddenTargets,
          order: idx + 1,
        });
      });

      await AppUtils.allSettled(addHiddenTargetsActions);
      return newSession.id;
    }, // prepareSessionOnClientSide()

    /**
     * @param {Object} session
     * @param {Object[]} sessionPrograms
     */
    async savePrepareSessionResultToIdbDb({ session, sessionPrograms }) {
      const isOfflineModeSupported = this.userHasPrivilege('offline/hasAccess') && this.hasOfflineModeClients;
      if (!isOfflineModeSupported) {
        return;
      }

      const idxDb = new GenericIndexedDb('sessions');

      try {
        await idxDb.set(session, session.id);
        idxDb.setCollectionPath(`sessions/${session.id}/sessionPrograms`);

        if (Array.isArray(sessionPrograms) && sessionPrograms.length) {
          const setSessionProgramsActions = sessionPrograms.map(async (sessionProgram) => {
            return idxDb.set(sessionProgram, sessionProgram.id);
          });
          await AppUtils.allSettled(setSessionProgramsActions);
        }
      } catch (err) {
        // Don't need to throw IDB error
      }
    }, // savePrepareSessionResultToIdbDb()

    async saveStartRunningSessionResultToIdbDb(sessionProgramDataMap) {
      const isOfflineModeSupported = this.userHasPrivilege('offline/hasAccess') && this.hasOfflineModeClients;
      if (!isOfflineModeSupported || !sessionProgramDataMap) {
        return;
      }

      const idxDb = new GenericIndexedDb('data');

      const setDataActions = Object.entries(sessionProgramDataMap).map(async ([id, data]) => {
        return idxDb.create(data, id);
      });
      await AppUtils.allSettled(setDataActions);
    }, // saveStartRunningSessionResultToIdbDb()

    async startRunningSessionViaBackend({
      sessionId,
      initialProgramId = null,
      appointmentId = null,
      sessionGroupId = null,
      userId = null,
    }) {
      const { data } = await callFirebaseFunction('startRunningSession', {
        sessionId,
        initialProgramId,
        appointmentId,
        sessionGroupId,
        userId,
        clientTimestamp: Timestamp.now().toMillis(),
      });
      await this.saveStartRunningSessionResultToIdbDb(data);
    }, // startRunningSessionViaBackend()

    /**
     * @param programId
     * @return {Promise<Array<string>>}
     */
    async getHiddenTargets(programId) {
      const now = Date.now();

      const programTargetsDb = new ProgramTargetsDB(programId);
      const masteredTargets = programTargetsDb.readAll([['status', '==', 'mastered']]);

      return chain(masteredTargets)
        .map((target) => {
          const lastHit = get(target, 'lastHit');
          if (!(lastHit instanceof Timestamp)) {
            return null;
          }

          const daysSinceLastHit = (now - lastHit.toDate().getTime()) / (1000 * 60 * 60 * 24);
          if (
            (Math.floor(daysSinceLastHit + 0.5) < get(target, 'maintenanceInterval', 1))
                && (daysSinceLastHit > 0.5) // Always allow user to see same-day targets
          ) {
            // A mastered target in maintenance will be shown if:
            // It was last hit within the previous 12 hours (to allow multiple sessions the same day)
            // OR It was last hit more than maintenance interval - 12 hours ago (If a target is on
            // weekly maintenance and it was hit Monday 1:00pm and the next session is started the
            // following Monday at 8:00am it should still be included even though it's technically
            // less than 7 * 24 hours ago it was last hit)
            return target.id;
          }

          return null;
        })
        .compact()
        .value();
    }, // getHiddenTargets()

    async prepareSession(originalSessionId) {
      if (!this.offlineMode) {
        const { data } = await callFirebaseFunction('prepareSessionV2', { sessionId: originalSessionId });
        const newSessionId = get(data, ['session', 'id'], null);
        await this.savePrepareSessionResultToIdbDb(data);
        return newSessionId;
      }
      return this.prepareSessionOnClientSide(originalSessionId);
    }, // prepareSession()

  },
};
